const STEPS = {
  BASIC: 'AddPanelBasic',
  NETWORK: 'AddPanelNetwork',
  ACCESS: 'AddPanelAccess',
}

const LIMITS = {
  DTMF_MAX_LENGTH: 10,
  OPEN_LINK_MAX_LENGTH: 500,
  MAX_DEVICES: 2,
  MIN_DEVICES: 1,
  PORT_MIN: 1,
  PORT_MAX: 65535,
}

const IP_PATTERN =
  /^(25[0-5]|2[0-4]\d|[01]?\d\d?)(\.(25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/

// IPv4 blocks that are not globally routable public unicast
// (RFC 1918 / 5735 / 6598 / 6890): [network address, prefix length].
const NON_PUBLIC_IPV4_BLOCKS = [
  ['0.0.0.0', 8], // this network
  ['10.0.0.0', 8], // private
  ['100.64.0.0', 10], // CGNAT
  ['127.0.0.0', 8], // loopback
  ['169.254.0.0', 16], // link-local
  ['172.16.0.0', 12], // private
  ['192.0.0.0', 24], // IETF protocol assignments
  ['192.0.2.0', 24], // TEST-NET-1
  ['192.88.99.0', 24], // 6to4 relay (deprecated)
  ['192.168.0.0', 16], // private
  ['198.18.0.0', 15], // benchmarking
  ['198.51.100.0', 24], // TEST-NET-2
  ['203.0.113.0', 24], // TEST-NET-3
  ['224.0.0.0', 4], // multicast
  ['240.0.0.0', 4], // reserved, includes 255.255.255.255
]

function toUint32(ip) {
  return ip.split('.').reduce((acc, octet) => acc * 256 + Number(octet), 0)
}

export function isValidIpAddress(ip) {
  if (!ip) return true

  const [ipPart, portPart, ...extra] = ip.split(':')

  if (extra.length > 0 || !IP_PATTERN.test(ipPart)) return false

  if (portPart !== undefined) {
    const port = Number(portPart)
    if (
      !Number.isInteger(port) ||
      port < LIMITS.PORT_MIN ||
      port > LIMITS.PORT_MAX
    ) {
      return false
    }
  }

  return true
}

export function isPublicIpAddress(ip) {
  if (!ip) return true

  const [ipPart] = ip.trim().split(':')

  if (!IP_PATTERN.test(ipPart)) return false

  const value = toUint32(ipPart)

  return !NON_PUBLIC_IPV4_BLOCKS.some(([network, prefix]) => {
    const blockSize = 2 ** (32 - prefix)
    return (
      Math.floor(value / blockSize) === Math.floor(toUint32(network) / blockSize)
    )
  })
}

// Single source of truth for the IP field: used both by the live check in
// AddPanelNetwork.vue and by the submit rule below. Empty input returns no
// error — the required rule already covers it.
export function getIpAddressError(ipAddress) {
  const value = (ipAddress || '').trim()

  if (!value) return ''
  if (!isValidIpAddress(value)) return 'Некорректный IP-адрес'
  if (!isPublicIpAddress(value)) return 'Необходимо ввести внешний IP-адрес!'

  return ''
}

export function isValidUrl(url) {
  if (!url) return true
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function isUniqueIpAddress(
  ipAddress,
  existingPanels,
  currentPanelId = null
) {
  if (!ipAddress) return true

  const normalized = ipAddress.trim().toLowerCase()

  return !existingPanels.some(panel => {
    if (currentPanelId && panel.panel_id === currentPanelId) return false
    return panel.ip_address?.trim().toLowerCase() === normalized
  })
}

export function isValidDtmf(dtmf) {
  if (!dtmf?.trim()) return false
  return dtmf.length <= LIMITS.DTMF_MAX_LENGTH
}

export function isValidOpenLink(link) {
  if (!link) return true
  return link.length <= LIMITS.OPEN_LINK_MAX_LENGTH
}

export function validateDevices(devices) {
  const active = devices.filter(d => d.deviceTypeId)

  if (active.length < LIMITS.MIN_DEVICES) {
    return {
      isValid: false,
      error: 'Необходимо добавить хотя бы одно управляемое устройство',
    }
  }

  if (active.length > LIMITS.MAX_DEVICES) {
    return {
      isValid: false,
      error: `Максимальное количество управляемых устройств — ${LIMITS.MAX_DEVICES}`,
    }
  }

  for (const device of active) {
    if (!device.usesApi && !device.dtmf?.trim()) {
      return {
        isValid: false,
        error: 'Укажите DTMF-код для устройства в DTMF-режиме',
      }
    }

    if (device.dtmf && !isValidDtmf(device.dtmf)) {
      return {
        isValid: false,
        error: `DTMF код не может быть длиннее ${LIMITS.DTMF_MAX_LENGTH} символов`,
      }
    }
  }

  return { isValid: true }
}

function buildPanelRules(panelData, existingPanels, currentPanelId) {
  const { basic, network, access } = panelData

  const ipAddressError = getIpAddressError(network.ipAddress)

  return [
    {
      condition: !basic?.name?.trim(),
      field: 'name',
      message: 'Пожалуйста, введите название панели',
      step: STEPS.BASIC,
    },
    {
      condition: !basic?.sipAccount?.trim(),
      field: 'sipAccount',
      message: 'Пожалуйста, введите SIP аккаунт панели',
      step: STEPS.BASIC,
    },
    {
      condition: !network.manufacturer,
      field: 'manufacturer',
      message: 'Пожалуйста, выберите производителя',
      step: STEPS.NETWORK,
    },
    {
      condition: !network.ipAddress?.trim(),
      field: 'ipAddress',
      message: 'Пожалуйста, введите IP-адрес',
      step: STEPS.NETWORK,
    },
    {
      condition: Boolean(ipAddressError),
      field: 'ipAddress',
      message: ipAddressError,
      step: STEPS.NETWORK,
    },
    {
      condition: !network.login?.trim(),
      field: 'login',
      message: 'Пожалуйста, введите логин',
      step: STEPS.NETWORK,
    },
    {
      // password is required only when creating a panel; on edit an empty field keeps the stored one
      condition: !currentPanelId && !network.password?.trim(),
      field: 'password',
      message: 'Пожалуйста, введите пароль',
      step: STEPS.NETWORK,
    },
    // Uncomment to enable IP uniqueness validation:
    // {
    //   condition:
    //     network.ipAddress &&
    //     !isUniqueIpAddress(network.ipAddress, existingPanels, currentPanelId),
    //   field: 'ipAddress',
    //   message: 'Панель с таким IP адресом уже существует',
    //   step: STEPS.NETWORK,
    // },
    // Uncomment to enable RTSP URL validation:
    // {
    //   condition: network.rtspUrl && !isValidUrl(network.rtspUrl),
    //   field: 'rtspUrl',
    //   message: 'Некорректный RTSP URL',
    //   step: STEPS.NETWORK,
    // },
    (() => {
      const result = validateDevices(access)
      return {
        condition: !result.isValid,
        field: 'devices',
        message: result.error,
        step: STEPS.ACCESS,
      }
    })(),
  ]
}

export function validatePanelData(
  panelData,
  existingPanels = [],
  currentPanelId = null
) {
  const rules = buildPanelRules(panelData, existingPanels, currentPanelId)

  const errors = rules
    .filter(rule => rule.condition)
    .map(({ field, message, step }) => ({ field, message, step }))

  return { isValid: errors.length === 0, errors }
}

export function getErrorMessage(errors) {
  return errors[0]?.message || ''
}

export function getFirstErrorStep(errors) {
  return errors[0]?.step || null
}
